import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CartItem, Product } from 'types/schema';
import LazyImage from 'components/Image/LazyImage';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import generateProductSlug from 'utils/generateProductSlug';
import { Description } from '../CheckoutStep1';
import useProductUomInfo from 'hooks/useProductUomInfo';
import Money from 'components/Money/Money';
import calculateVAT from 'utils/calculateVAT';
import deleteIcon from '../icons/deleteIcon.svg';
import noImage from '../icons/noIMG.svg';

const ProductRowDesktop = styled.div`
  position: relative;
  background: #fff;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: auto 140px 120px 120px;
  column-gap: 16px;
  align-items: center;
  min-height: 112px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  align-items: center;
  opacity: 0.5;
`;

const ProductImage = styled(({ hasNoImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 80px;
  height: 80px;
  border-radius: 4px;
  object-fit: ${({ hasNoImage }) => (hasNoImage ? 'contain' : 'cover')};
  ${({ hasNoImage }) => (hasNoImage ? `opacity: 0.3;` : '')};
  flex-shrink: 0;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ProductUom = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #6b7280;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Price = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #111827;
`;

const OriginalPrice = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const Quantity = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #111827;
  opacity: 0.5;
`;

const TotalBeforeVat = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #111827;
  text-align: right;
  opacity: 0.5;
`;

const TotalAfterVatContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  opacity: 0.5;
`;

const TotalAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: #111827;
  text-align: right;
`;

const VatText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b5563;
  text-align: right;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  background: #fff;

  &:hover {
    background: #f9fafb;
  }
`;

const ProductRowMobile = styled.div`
  position: relative;
  background: #fff;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 88px;
`;

const MobileInfo = styled.div`
  display: flex;
  flex: 1;
  gap: 12px;
  align-items: center;
  opacity: 0.5;
  min-width: 0;
`;

const MobileImage = styled(({ hasNoImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  object-fit: ${({ hasNoImage }) => (hasNoImage ? 'contain' : 'cover')};
  ${({ hasNoImage }) => (hasNoImage ? `opacity: 0.3;` : '')};
  flex-shrink: 0;
`;

export interface NotAvailableProductItemProps {
  cartItem: CartItem;
  lang: string;
  isDesktop: boolean;
  onRemove?: (item: CartItem) => void;
}

const NotAvailableProductItem: FC<NotAvailableProductItemProps> = ({ cartItem, lang, isDesktop, onRemove }) => {
  const { uomInfo } = useProductUomInfo(cartItem?.product as Product);

  const pdpUrl = `/products/${generateProductSlug(
    cartItem?.product?.name || '',
    cartItem?.product?.supplierInternalCode || '',
    lang,
  )}`;

  const unitPrice = useMemo(
    () =>
      calculateVAT(
        cartItem?.product?.priceV2?.price || 0,
        cartItem?.product?.priceV2?.vat || 0,
        !!cartItem?.product?.priceV2?.vatIncluded,
      ).beforeVat,
    [cartItem],
  );

  const originalUnitPrice = useMemo(() => {
    const origPrice = cartItem?.product?.priceV2?.originalPrice || 0;

    if (origPrice <= (cartItem?.product?.priceV2?.price || 0)) return null;

    return calculateVAT(origPrice, cartItem?.product?.priceV2?.vat || 0, !!cartItem?.product?.priceV2?.vatIncluded)
      .beforeVat;
  }, [cartItem]);

  const totalBeforeVat = unitPrice * (cartItem?.quantity || 0);

  const totalAfterVat = useMemo(() => {
    const vatAmount = Math.round((totalBeforeVat * (cartItem?.product?.priceV2?.vat || 0)) / 100);

    return totalBeforeVat + vatAmount;
  }, [totalBeforeVat, cartItem]);

  if (!isDesktop) {
    return (
      <ProductRowMobile>
        <MobileInfo>
          <Link to={pdpUrl}>
            <MobileImage
              hasNoImage={!cartItem?.product?.imageUrl}
              src={cartItem?.product?.imageUrl || noImage}
              alt={cartItem?.product?.name}
            />
          </Link>

          <ProductDetails>
            <div>
              <Link to={pdpUrl}>
                <ProductName>
                  <Description>{cartItem?.product?.name}</Description>
                </ProductName>
              </Link>

              <ProductUom>{uomInfo}</ProductUom>
            </div>

            <PriceRow>
              <Price><Money>{unitPrice}</Money></Price>

              {originalUnitPrice && <OriginalPrice><Money>{originalUnitPrice}</Money></OriginalPrice>}
            </PriceRow>
          </ProductDetails>
        </MobileInfo>

        {onRemove && (
          <DeleteButton onClick={() => onRemove(cartItem)}>
            <SVGIcon src={deleteIcon} renderAsImage={false} />
          </DeleteButton>
        )}
      </ProductRowMobile>
    );
  }

  return (
    <ProductRowDesktop>
      <ProductInfo>
        <Link to={pdpUrl}>
          <ProductImage
            hasNoImage={!cartItem?.product?.imageUrl}
            src={cartItem?.product?.imageUrl || noImage}
            alt={cartItem?.product?.name}
          />
        </Link>

        <ProductDetails>
          <div>
            <Link to={pdpUrl}>
              <ProductName>
                <Description>{cartItem?.product?.name}</Description>
              </ProductName>
            </Link>

            <ProductUom>{uomInfo}</ProductUom>
          </div>

          <PriceRow>
            <Price><Money>{unitPrice}</Money></Price>

            {originalUnitPrice && <OriginalPrice><Money>{originalUnitPrice}</Money></OriginalPrice>}
          </PriceRow>
        </ProductDetails>
      </ProductInfo>

      <Quantity>x{cartItem?.quantity}</Quantity>

      <TotalBeforeVat><Money>{totalBeforeVat}</Money></TotalBeforeVat>

      <TotalAfterVatContainer>
        <TotalAmount><Money>{totalAfterVat}</Money></TotalAmount>

        <VatText>(VAT: {cartItem?.product?.vat || 0}%)</VatText>
      </TotalAfterVatContainer>

      {onRemove && (
        <DeleteButton onClick={() => onRemove(cartItem)}>
          <SVGIcon src={deleteIcon} renderAsImage={false} />
        </DeleteButton>
      )}
    </ProductRowDesktop>
  );
};

export default NotAvailableProductItem;
